import React, { useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useDocumentViewer } from '../../contexts/DocumentViewerContext';
import { useAskSidebar } from '../../contexts/AskSidebarContext';
import { DocumentPanel } from '../citations/DocumentPanel';
import { documentApi } from '../../services/api';
import { DocumentsExplorer } from './documents/DocumentsExplorer';

interface MetadataExtractionResponse {
  documentTypes: Array<{ documentId: string; type: string; confidence: number; summary?: string; citations?: any[] }>;
  [key: string]: any;
}

/**
 * Documents tab — shows the active document (via DocumentPanel) when a citation
 * is clicked, or the DocumentsExplorer with summary cards, search, timeline,
 * and bookmarks when nothing is open.
 *
 * When maximised with a document open, renders a split layout:
 * Explorer (left) | Document + TOC (right).
 */
export const DocumentsTab: React.FC = () => {
  const { activeDocument, openDocument, closeDocument } = useDocumentViewer();
  const { pageContext, isMaximized } = useAskSidebar();

  // Fetch current document details to get caseId
  const { data: docDetails } = useQuery(
    ['document', activeDocument?.documentId],
    () => documentApi.getDocument(activeDocument!.documentId),
    { enabled: !!activeDocument?.documentId, staleTime: 5 * 60 * 1000 },
  );

  // Fetch all case documents for the dropdown and navigator
  const caseId = docDetails?.caseId || pageContext.caseId;
  const { data: caseDocuments } = useQuery(
    ['documents', caseId],
    () => documentApi.getCaseDocuments(caseId!),
    { enabled: !!caseId, staleTime: 5 * 60 * 1000 },
  );

  // Read metadata from React Query cache (populated by useMetadataExtraction in CaseDetailPage)
  const { data: metadata } = useQuery<MetadataExtractionResponse | null>(
    ['metadata', caseId],
    { enabled: false },
  );

  const sortedDocuments = useMemo(() => {
    if (!caseDocuments) return [];
    return [...caseDocuments]
      .filter((d: any) => d.documentType !== 'dicom_image')
      .sort((a, b) => a.filename.localeCompare(b.filename));
  }, [caseDocuments]);

  const handleSwitchDocument = useCallback((documentId: string) => {
    const doc = sortedDocuments.find(d => d.documentId === documentId);
    if (doc) {
      openDocument({ documentId: doc.documentId, documentName: doc.filename });
    }
  }, [sortedDocuments, openDocument]);

  // ─── Maximised split layout: Explorer | Document + TOC ────────────
  if (isMaximized && activeDocument) {
    return (
      <div className="flex-1 flex min-h-0 h-full">
        {/* Left: Explorer */}
        <div className="w-[380px] flex-shrink-0 flex flex-col min-h-0 border-r border-medical-200 dark:border-medical-700 bg-white dark:bg-medical-800">
          <div className="flex items-center px-3 py-1.5 border-b border-medical-100 dark:border-medical-700 bg-medical-50/50 dark:bg-medical-900/30">
            <span className="text-[10px] font-semibold text-medical-500 dark:text-medical-400 uppercase tracking-wide">
              Explorer
            </span>
          </div>
          {caseId ? (
            <DocumentsExplorer
              caseId={caseId}
              documents={sortedDocuments}
              metadata={metadata}
              onOpenDocument={openDocument}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-xs text-medical-400">Loading...</p>
            </div>
          )}
        </div>

        {/* Right: Document viewer + TOC */}
        <div className="flex-1 flex flex-col min-h-0">
          <DocumentPanel
            request={activeDocument}
            onClose={closeDocument}
            allDocuments={sortedDocuments}
            onSwitchDocument={handleSwitchDocument}
            caseId={caseId}
          />
        </div>
      </div>
    );
  }

  // ─── Document open (normal sidebar) ───────────────────────────────
  if (activeDocument) {
    return (
      <div className="flex-1 flex flex-col min-h-0">
        <DocumentPanel
          request={activeDocument}
          onClose={closeDocument}
          allDocuments={sortedDocuments}
          onSwitchDocument={handleSwitchDocument}
          caseId={caseId}
        />
      </div>
    );
  }

  // ─── No document open — explorer only ─────────────────────────────
  return (
    <div className="flex-1 flex flex-col min-h-0">
      {caseId ? (
        <DocumentsExplorer
          caseId={caseId}
          documents={sortedDocuments}
          metadata={metadata}
          onOpenDocument={openDocument}
        />
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center text-center px-6 pb-8">
          <p className="text-xs text-medical-400 dark:text-medical-500 leading-relaxed">
            Loading documents...
          </p>
        </div>
      )}
    </div>
  );
};
